import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import api from '../api';
import { useIzin } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const TL = (v: number) =>
  `₺${Math.abs(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const AY = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];
const ayAdi = (s: string) => { const [y, m] = s.split('-'); return `${AY[parseInt(m)-1]} ${y}`; };

type Baslik = { id: number; tip: 'gelir' | 'gider'; ad: string; etkinlik_turu: string | null };
type AyRow  = { ay: string; gelir: number; gider: number; net: number };
type Islem  = { id: string; tarih: string; tutar: number; tip: 'gelir'|'gider'; aciklama: string; baslik: string; ilgili_kisi: string|null; odeme_yontemi: string|null; kaynak: string; bilet_sayisi: number|null };
type KirilimKalem = { ad: string; tutar: number; oran: number };
type Ozet   = {
  gelir: number; gider: number; net: number; bekleyen_hakedis: number;
  onceki_donem: { gelir: number; gider: number; net: number } | null;
  kategori_kirilim: { gelir: KirilimKalem[]; gider: KirilimKalem[] };
};

const KAYNAK_ETIKET: Record<string, string> = { manuel: 'Manuel', ogrenci: 'Öğrenci', egitmen: 'Eğitmen' };

export default function Muhasebe() {
  usePageTitle('Muhasebe');
  const bugun = new Date().toISOString().slice(0, 10);
  const buAy  = bugun.slice(0, 7);
  const canMuhasebe = useIzin('muhasebe');

  const [baslangic, setBaslangic] = useState(bugun);
  const [bitis, setBitis]         = useState(bugun);
  const [tipFiltre, setTipFiltre] = useState<'hepsi'|'gelir'|'gider'>('hepsi');

  const [ozet, setOzet]       = useState<Ozet|null>(null);
  const [aylik, setAylik]     = useState<AyRow[]>([]);
  const [islemler, setIslemler] = useState<Islem[]>([]);
  const [basliklar, setBasliklar] = useState<Baslik[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);

  // Modal state
  const [modal, setModal]     = useState<'gelir'|'gider'|null>(null);
  const [form, setForm]       = useState({ baslik_id: '', tutar: '', aciklama: '', tarih: bugun, bilet_sayisi: '' });
  const [yeniBas, setYeniBas] = useState('');
  const [yeniBasEtkinlikTuru, setYeniBasEtkinlikTuru] = useState('');
  const [baslikEkMode, setBaslikEkMode] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata]       = useState('');

  const yukle = async () => {
    setYukleniyor(true);
    try {
      const p = `baslangic=${baslangic}&bitis=${bitis}`;
      const tP = tipFiltre !== 'hepsi' ? `&tip=${tipFiltre}` : '';
      const [oRes, aRes, iRes, bRes] = await Promise.all([
        api.get(`/api/muhasebe/ozet?${p}`),
        api.get('/api/muhasebe/aylik'),
        api.get(`/api/muhasebe/islemler?${p}${tP}`),
        api.get('/api/muhasebe/basliklar'),
      ]);
      setOzet(oRes.data);
      setAylik(aRes.data);
      setIslemler(iRes.data);
      setBasliklar(bRes.data);
    } catch (e) { console.error(e); }
    finally { setYukleniyor(false); }
  };

  useEffect(() => { yukle(); }, [baslangic, bitis, tipFiltre]);

  const modalAc = (tip: 'gelir'|'gider') => {
    const ilk = basliklar.find(b => b.tip === tip);
    setForm({ baslik_id: ilk ? String(ilk.id) : '', tutar: '', aciklama: '', tarih: bugun, bilet_sayisi: '' });
    setYeniBas(''); setYeniBasEtkinlikTuru(''); setBaslikEkMode(false); setHata('');
    setModal(tip);
  };

  const baslikEkle = async () => {
    if (!yeniBas.trim()) return;
    try {
      const res = await api.post('/api/muhasebe/basliklar', { tip: modal, ad: yeniBas.trim(), etkinlik_turu: yeniBasEtkinlikTuru || null });
      setBasliklar(p => [...p, res.data]);
      setForm(p => ({ ...p, baslik_id: String(res.data.id) }));
      setYeniBas(''); setYeniBasEtkinlikTuru(''); setBaslikEkMode(false);
    } catch { setHata('Başlık eklenemedi'); }
  };

  const islemKaydet = async () => {
    if (!form.baslik_id || !form.tutar || !form.tarih) { setHata('Başlık, tutar ve tarih zorunlu'); return; }
    setKaydediliyor(true); setHata('');
    try {
      await api.post('/api/muhasebe/islem', { tip: modal, ...form, tutar: parseFloat(form.tutar), bilet_sayisi: form.bilet_sayisi ? parseInt(form.bilet_sayisi) : null });
      setModal(null);
      yukle();
    } catch (e: any) { setHata(e.response?.data?.hata || 'Hata oluştu'); }
    finally { setKaydediliyor(false); }
  };

  const islemSil = async (id: string) => {
    if (!window.confirm('Bu işlemi silmek istiyor musunuz?')) return;
    try { await api.delete(`/api/muhasebe/islem/${id}`); yukle(); }
    catch { alert('Silinemedi'); }
  };

  const maxAy = Math.max(...aylik.map(a => Math.max(a.gelir, a.gider)), 1);
  const filtBasliklar = modal ? basliklar.filter(b => b.tip === modal) : [];
  const selectedBaslik = filtBasliklar.find(b => String(b.id) === form.baslik_id) || null;

  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      {/* Başlık + butonlar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: textPrimary }}>Muhasebe</div>
          <div style={{ fontSize: 13, color: textSec, marginTop: 2 }}>Gelir, gider ve nakit akışı</div>
        </div>
        <div />
      </div>

      {/* Dönem filtresi */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '12px 16px' }}>
        <span style={{ fontSize: 12, color: textSec, fontWeight: 500 }}>Dönem</span>
        <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
        <span style={{ color: textMuted }}>—</span>
        <input type="date" value={bitis} onChange={e => setBitis(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
        <button onClick={yukle} disabled={yukleniyor}
          style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#534AB7', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: yukleniyor ? 0.6 : 1 }}>
          {yukleniyor ? 'Yükleniyor...' : 'Filtrele'}
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {[['Bu ay', 0], ['Bu yıl', 1], ['Tüm zamanlar', 2]].map(([label, i]) => (
            <button key={label} onClick={() => {
              const n = new Date();
              if (i === 0) { setBaslangic(`${buAy}-01`); setBitis(bugun); }
              else if (i === 1) { setBaslangic(`${n.getFullYear()}-01-01`); setBitis(bugun); }
              else { setBaslangic('2020-01-01'); setBitis(bugun); }
            }} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #ddd', background: card, cursor: 'pointer', fontSize: 12, color: isDark ? '#bbb' : '#555' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI kartlar */}
      {ozet && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
          <Kart baslik="Toplam gelir"       deger={ozet.gelir}            renk="#1D9E75" bg="#EAF3DE" alt="Tüm gelir kalemleri"
            oncekiDeger={ozet.onceki_donem?.gelir} yuksekIyi />
          <Kart baslik="Toplam gider"       deger={ozet.gider}            renk="#e24b4a" bg="#FCEBEB" alt="Tüm gider kalemleri"
            oncekiDeger={ozet.onceki_donem?.gider} yuksekIyi={false} />
          <Kart baslik="Net"                deger={ozet.net}              renk={ozet.net>=0?'#085041':'#A32D2D'} bg={ozet.net>=0?'#EAF3DE':'#FCEBEB'} alt="Gelir − Gider" isaret
            oncekiDeger={ozet.onceki_donem?.net} yuksekIyi />
          <Kart baslik="Bekleyen hakedis"   deger={ozet.bekleyen_hakedis} renk="#854F0B" bg="#FAEEDA" alt="Henüz ödenmemiş" />
        </div>
      )}

      {/* Kategori kırılımı */}
      {ozet && (ozet.kategori_kirilim.gelir.length > 0 || ozet.kategori_kirilim.gider.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          <Kirilim baslik="Gelir Kırılımı" kalemler={ozet.kategori_kirilim.gelir} renk="#1D9E75" koyuRenk="#085041" />
          <Kirilim baslik="Gider Kırılımı" kalemler={ozet.kategori_kirilim.gider} renk="#e24b4a" koyuRenk="#A32D2D" />
        </div>
      )}

      {/* Aylık bar grafik */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '20px 20px 12px', marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Son 12 ay</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120, marginBottom: 6 }}>
          {aylik.map(row => (
            <div key={row.ay} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{ width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end', height: 100 }}>
                <div title={`Gelir: ${TL(row.gelir)}`} style={{ flex: 1, background: '#1D9E75', borderRadius: '3px 3px 0 0', height: `${(row.gelir / maxAy) * 100}%`, minHeight: row.gelir > 0 ? 3 : 0 }} />
                <div title={`Gider: ${TL(row.gider)}`} style={{ flex: 1, background: '#e24b4a', borderRadius: '3px 3px 0 0', height: `${(row.gider / maxAy) * 100}%`, minHeight: row.gider > 0 ? 3 : 0 }} />
              </div>
              <div style={{ fontSize: 9, color: isDark ? '#555' : '#bbb' }}>{AY[parseInt(row.ay.slice(5))-1]}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 11, color: textSec }}>
          <span><Nokta renk="#1D9E75" /> Gelir</span>
          <span><Nokta renk="#e24b4a" /> Gider</span>
        </div>
      </div>

      {/* Aylık tablo */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${border}`, background: cardHover }}>
              {['Ay','Gelir','Gider','Net'].map(h => (
                <th key={h} style={{ textAlign: h==='Ay'?'left':'right', padding: '10px 16px', fontSize: 11, color: textMuted, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...aylik].reverse().map((row, i) => (
              <tr key={row.ay} style={{ borderBottom: i < aylik.length-1 ? `1px solid ${borderSub}` : 'none' }}>
                <td style={{ padding: '9px 16px', fontWeight: 500 }}>{ayAdi(row.ay)}</td>
                <td style={{ padding: '9px 16px', textAlign: 'right', color: '#1D9E75', fontWeight: 500 }}>{row.gelir > 0 ? TL(row.gelir) : '—'}</td>
                <td style={{ padding: '9px 16px', textAlign: 'right', color: '#e24b4a', fontWeight: 500 }}>{row.gider > 0 ? TL(row.gider) : '—'}</td>
                <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 600, color: row.net > 0 ? '#085041' : row.net < 0 ? '#A32D2D' : textMuted }}>
                  {row.net === 0 ? '—' : (row.net > 0 ? '+' : '') + TL(row.net)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* İşlem listesi */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>İşlem geçmişi</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['hepsi','gelir','gider'] as const).map(t => (
              <button key={t} onClick={() => setTipFiltre(t)} style={{
                padding: '4px 12px', borderRadius: 20, border: '1px solid', fontSize: 12, cursor: 'pointer',
                borderColor: tipFiltre===t ? (t==='gelir'?'#1D9E75':t==='gider'?'#e24b4a':'#534AB7') : '#ddd',
                background:  tipFiltre===t ? (t==='gelir'?isDark?'rgba(29,158,117,0.15)':'#EAF3DE':t==='gider'?isDark?'rgba(228,75,74,0.15)':'#FCEBEB':isDark?'rgba(83,74,183,0.2)':'#EEEDFE') : card,
                color:       tipFiltre===t ? (t==='gelir'?'#085041':t==='gider'?'#A32D2D':'#3C3489') : isDark ? '#bbb' : '#555',
              }}>
                {t==='hepsi'?'Hepsi':t==='gelir'?'Gelir':'Gider'}
              </button>
            ))}
          </div>
        </div>
        {yukleniyor ? (
          <div style={{ padding: 32, textAlign: 'center', color: textMuted, fontSize: 13 }}>Yükleniyor...</div>
        ) : islemler.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: textMuted, fontSize: 13 }}>Bu dönemde işlem yok.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${border}`, background: cardHover }}>
                {['Tarih','Başlık','Açıklama','İlgili','Kaynak','Tutar',''].map(h => (
                  <th key={h} style={{ textAlign: h==='Tutar'?'right':'left', padding: '9px 14px', fontSize: 11, color: textMuted, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {islemler.map((ism, i) => (
                <tr key={`${ism.kaynak}-${ism.id}`} style={{ borderBottom: i < islemler.length-1 ? `1px solid ${borderSub}` : 'none' }}>
                  <td style={{ padding: '9px 14px', color: isDark ? '#777' : '#777', whiteSpace: 'nowrap' }}>{ism.tarih?.slice(0,10)}</td>
                  <td style={{ padding: '9px 14px', fontWeight: 500 }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, marginRight: 6,
                      background: ism.tip==='gelir'?isDark?'rgba(29,158,117,0.15)':'#EAF3DE':isDark?'rgba(228,75,74,0.15)':'#FCEBEB',
                      color:      ism.tip==='gelir'?'#085041':'#A32D2D',
                    }}>{ism.tip==='gelir'?'Gelir':'Gider'}</span>
                    {ism.baslik}
                    {ism.bilet_sayisi != null && (
                      <span style={{ marginLeft: 6, background: '#E0F2FE', color: '#0369A1', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 600 }}>
                        {ism.bilet_sayisi} bilet
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '9px 14px', color: isDark ? '#999' : '#666', fontSize: 12 }}>{ism.aciklama || '—'}</td>
                  <td style={{ padding: '9px 14px', color: textSec, fontSize: 12 }}>{ism.ilgili_kisi || '—'}</td>
                  <td style={{ padding: '9px 14px' }}>
                    <span style={{ background: isDark ? '#222222' : '#f0f0f0', color: isDark ? '#999' : '#666', borderRadius: 20, padding: '2px 8px', fontSize: 10 }}>
                      {KAYNAK_ETIKET[ism.kaynak] || ism.kaynak}
                    </span>
                  </td>
                  <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 600, color: ism.tip==='gelir'?'#1D9E75':'#e24b4a' }}>
                    {ism.tip==='gelir'?'+':'-'}{TL(parseFloat(String(ism.tutar)))}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right' }}>
                    {ism.kaynak === 'manuel' && canMuhasebe && (
                      <button onClick={() => islemSil(ism.id)} title="Sil"
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ccc', fontSize: 15, lineHeight: 1 }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#e24b4a')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#ccc')}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Gelir / Gider ekleme modali */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: 420 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>
              {modal === 'gelir' ? '+ Gelir ekle' : '− Gider ekle'}
            </div>

            {/* Başlık seç */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: textSec, marginBottom: 6, fontWeight: 500, display: 'flex', justifyContent: 'space-between' }}>
                <span>Başlık</span>
                <button onClick={() => setBaslikEkMode(p => !p)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: '#534AB7' }}>
                  {baslikEkMode ? 'İptal' : '+ Yeni başlık'}
                </button>
              </div>
              {baslikEkMode ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input value={yeniBas} onChange={e => setYeniBas(e.target.value)}
                      placeholder="Başlık adı" onKeyDown={e => e.key === 'Enter' && baslikEkle()}
                      style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #534AB7', fontSize: 13 }} />
                    <button onClick={baslikEkle}
                      style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#534AB7', color: 'white', cursor: 'pointer', fontSize: 13 }}>
                      Ekle
                    </button>
                  </div>
                  {modal === 'gelir' && (
                    <select value={yeniBasEtkinlikTuru} onChange={e => setYeniBasEtkinlikTuru(e.target.value)}
                      style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, color: isDark ? '#bbb' : '#555' }}>
                      <option value="">Bilet takibi yok (standart)</option>
                      <option value="milonga">Milonga</option>
                      <option value="pratik">Pratik</option>
                      <option value="etkinlik">Diğer etkinlik</option>
                    </select>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {filtBasliklar.map(b => (
                    <button key={b.id} onClick={() => setForm(p => ({ ...p, baslik_id: String(b.id) }))}
                      style={{
                        padding: '6px 14px', borderRadius: 20, border: '1px solid', cursor: 'pointer', fontSize: 12,
                        borderColor: form.baslik_id === String(b.id) ? (modal==='gelir'?'#1D9E75':'#e24b4a') : '#ddd',
                        background:  form.baslik_id === String(b.id) ? (modal==='gelir'?isDark?'rgba(29,158,117,0.15)':'#EAF3DE':isDark?'rgba(228,75,74,0.15)':'#FCEBEB') : card,
                        color:       form.baslik_id === String(b.id) ? (modal==='gelir'?'#085041':'#A32D2D') : isDark ? '#bbb' : '#555',
                        fontWeight:  form.baslik_id === String(b.id) ? 600 : 400,
                      }}>
                      {b.ad}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Tutar + Tarih */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>Tutar (TL)</div>
                <input type="number" value={form.tutar} onChange={e => setForm(p => ({ ...p, tutar: e.target.value }))}
                  placeholder="0.00" min="0" step="0.01"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>Tarih</div>
                <input type="date" value={form.tarih} onChange={e => setForm(p => ({ ...p, tarih: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Açıklama */}
            <div style={{ marginBottom: selectedBaslik?.etkinlik_turu ? 10 : 16 }}>
              <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>Açıklama (opsiyonel)</div>
              <input value={form.aciklama} onChange={e => setForm(p => ({ ...p, aciklama: e.target.value }))}
                placeholder="Örn: Nisan milonga etkinliği"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
            </div>

            {/* Bilet sayısı — sadece etkinlik_turu olan başlıklarda */}
            {selectedBaslik?.etkinlik_turu && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>
                  Bilet / Katılımcı Sayısı
                  <span style={{ marginLeft: 6, background: '#E0F2FE', color: '#0369A1', borderRadius: 10, padding: '1px 6px', fontSize: 10 }}>
                    {selectedBaslik.etkinlik_turu}
                  </span>
                </div>
                <input type="number" value={form.bilet_sayisi} onChange={e => setForm(p => ({ ...p, bilet_sayisi: e.target.value }))}
                  placeholder="0" min="0" step="1"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #0369A1', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            )}

            {hata && <div style={{ background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{hata}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)}
                style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${border}`, background: card, cursor: 'pointer', fontSize: 13 }}>
                İptal
              </button>
              <button onClick={islemKaydet} disabled={kaydediliyor}
                style={{ padding: '8px 20px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  background: modal==='gelir' ? '#1D9E75' : '#e24b4a', color: 'white' }}>
                {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kart({ baslik, deger, renk, bg, alt, isaret, oncekiDeger, yuksekIyi }: {
  baslik: string; deger: number; renk: string; bg: string; alt: string; isaret?: boolean;
  oncekiDeger?: number; yuksekIyi?: boolean;
}) {
  const { isDark } = useTheme();
  // Önceki eşit uzunluktaki döneme göre değişim — 0'a bölme riski varsa (önceki dönem 0'sa) gösterilmez
  let delta: { yuzde: number; iyi: boolean } | null = null;
  if (oncekiDeger != null && oncekiDeger !== 0) {
    const yuzde = Math.round(((deger - oncekiDeger) / Math.abs(oncekiDeger)) * 100);
    if (yuzde !== 0) {
      const artti = yuzde > 0;
      delta = { yuzde, iyi: yuksekIyi === undefined ? true : (yuksekIyi ? artti : !artti) };
    }
  }
  const iyiRenk = isDark ? '#3B6D11' : '#085041', iyiBg = isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE';
  const kotuRenk = isDark ? '#e24b4a' : '#A32D2D', kotuBg = isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB';
  return (
    <div style={{ background: isDark ? '#1a1a1a' : 'white', border: `1px solid ${isDark ? '#2a2a2a' : '#eee'}`, borderRadius: 10, padding: '16px 18px' }}>
      <div style={{ fontSize: 11, color: isDark ? '#999' : '#888', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{baslik}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: renk }}>
        {isaret && deger > 0 ? '+' : ''}{TL(deger)}
      </div>
      <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ background: bg, color: renk, borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>{alt}</span>
        {delta && (
          <span title="Önceki eşit uzunluktaki döneme göre"
            style={{ background: delta.iyi ? iyiBg : kotuBg, color: delta.iyi ? iyiRenk : kotuRenk, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
            {delta.yuzde > 0 ? '▲' : '▼'} %{Math.abs(delta.yuzde)}
          </span>
        )}
      </div>
    </div>
  );
}

function Kirilim({ baslik, kalemler, renk, koyuRenk }: { baslik: string; kalemler: KirilimKalem[]; renk: string; koyuRenk: string }) {
  const { isDark } = useTheme();
  const card      = isDark ? '#1a1a1a' : 'white';
  const border    = isDark ? '#2a2a2a' : '#eee';
  const textSec   = isDark ? '#999'    : '#888';
  const textMuted = isDark ? '#666'    : '#aaa';
  const maks = Math.max(...kalemler.map(k => k.tutar), 1);
  return (
    <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '16px 18px' }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>{baslik}</div>
      {kalemler.length === 0 ? (
        <div style={{ fontSize: 12, color: textMuted, textAlign: 'center', padding: '16px 0' }}>Bu dönemde kayıt yok</div>
      ) : (
        <div style={{ display: 'grid', gap: 9 }}>
          {kalemler.map(k => (
            <div key={k.ad} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 110, fontSize: 11, color: textSec, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }} title={k.ad}>
                {k.ad}
              </div>
              <div style={{ flex: 1, height: 16, background: isDark ? '#222222' : '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${(k.tutar / maks) * 100}%`, height: '100%', background: renk, borderRadius: 3, transition: 'width 0.6s ease', minWidth: k.tutar > 0 ? 3 : 0 }} />
              </div>
              <div style={{ width: 110, fontSize: 11, color: koyuRenk, fontWeight: 600, textAlign: 'right', flexShrink: 0 }}>
                {TL(k.tutar)} <span style={{ color: textMuted, fontWeight: 400 }}>· %{k.oran}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Nokta({ renk }: { renk: string }) {
  return <span style={{ display: 'inline-block', width: 8, height: 8, background: renk, borderRadius: 2, marginRight: 4 }} />;
}
